import React, { useEffect, useMemo, useState } from 'react';
import { HEALTH_SCOPE_STATUS, selectHealthScopes } from '../healthScopes';
import { resolveHealthV12Mode } from '../healthV12Flag';
import {
  coverageCalloutCopy,
  summarizeCoverageLayers,
} from '../healthCapability';
import InfoTooltip from './InfoTooltip';
import './HealthScoreDetails.css';

const ENDPOINTS_GAUGE_VIEW = Object.freeze({
  width: 1600,
  height: 800,
  viewBoxX: 290,
  /* Tight crop around the visible gauge geometry. */
  viewBoxY: 154,
  viewBoxWidth: 1020,
  /* Crop empty space under the dial so the card is shorter. */
  viewBoxHeight: 470,
  cx: 800,
  cy: 640,
  tickInnerRadius: 305,
  tickOuterRadius: 445,
  tickCount: 32,
});

/** % from top of `.health-score-stage-bundle` matching SVG y → middle of gauge bowl (inner apex → arc center). */
function getEndpointsGaugeResolvedLabelAnchorYPct(view = ENDPOINTS_GAUGE_VIEW) {
  const bowlMidY = (view.cy - view.tickInnerRadius + view.cy) / 2;
  return ((bowlMidY - view.viewBoxY) / view.viewBoxHeight) * 100;
}

function buildGaugeTicks({
  count,
  cx,
  cy,
  innerRadius,
  outerRadius,
  scorePercent = 0,
}) {
  const score = Math.max(0, Math.min(100, Number(scorePercent) || 0));
  return Array.from({ length: count + 1 }, (_, idx) => {
    const t = idx / count;
    const degrees = 180 + t * 180;
    const radians = (degrees * Math.PI) / 180;
    let x1 = cx + Math.cos(radians) * innerRadius;
    const y1 = cy + Math.sin(radians) * innerRadius;
    let x2 = cx + Math.cos(radians) * outerRadius;
    const y2 = cy + Math.sin(radians) * outerRadius;
    // Only the exact top-center tick can collapse into a degenerate bbox with glow filters.
    // Keep the correction tiny and scoped so neighboring ticks stay perfectly radial/aligned.
    if (Math.abs(x2 - x1) < 1e-4) {
      x1 -= 0.12;
      x2 += 0.12;
    }
    const edgeDistance = Math.min(degrees - 180, 360 - degrees);
    const fade = Math.min(1, edgeDistance / 18);
    const opacity = (0.25 + 0.7 * fade).toFixed(2);
    /* Lit through the needle position so the tip never sits on an empty tick. */
    const tickPercent = t * 100;
    const active = score > 0 && tickPercent <= score + 1e-6;
    return { key: `tick-${idx}`, x1, y1, x2, y2, opacity, active };
  });
}

const EndpointsGaugeDefs = () => (
  <defs id="endpoints-gauge-defs" data-gauge-svg-section="defs">
    <g id="endpoints-gauge-def-filters" data-gauge-def-kind="filters">
      <filter id="tickGlow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="1.6" result="b" />
        <feMerge>
          <feMergeNode in="b" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </g>
  </defs>
);

/**
 * Frontend-only presentation colour for the dial.
 * Prefer the authoritative backend grade when present; numeric score bands are
 * a temporary fallback until v1.1 always supplies grade/status.
 */
function getGaugeActiveStroke(scorePercent, grade = null) {
  const g = String(grade || '').trim().toUpperCase();
  if (g === 'A' || g === 'B') return '#1ea554';
  if (g === 'C') return '#f59e0b';
  if (g === 'D' || g === 'F') return '#ef4444';
  const s = Math.max(0, Math.min(100, Number(scorePercent) || 0));
  if (s <= 30) return '#ef4444';
  if (s <= 65) return '#f59e0b';
  return '#1ea554';
}

const EndpointsGaugeTicks = ({ ticks, activeStroke, inactiveStroke = '#404040' }) => (
  <g
    id="endpoints-gauge-ticks"
    className="endpoints-gauge-layer"
    data-gauge-part="ticks"
    shapeRendering="geometricPrecision"
  >
    {ticks.map((tick, tickIdx) => (
      <line
        key={tick.key}
        data-tick-index={tickIdx}
        x1={tick.x1}
        y1={tick.y1}
        x2={tick.x2}
        y2={tick.y2}
        stroke={tick.active ? activeStroke : inactiveStroke}
        strokeWidth="24"
        strokeLinecap="round"
        opacity={tick.active ? tick.opacity : '0.9'}
        filter={undefined}
      />
    ))}
  </g>
);

const SCOPE_STATUS_TEXT = Object.freeze({
  [HEALTH_SCOPE_STATUS.LOADING]: 'Loading score…',
  [HEALTH_SCOPE_STATUS.UNAVAILABLE]: 'Score unavailable',
  [HEALTH_SCOPE_STATUS.INSUFFICIENT_DATA]: 'Insufficient data',
  [HEALTH_SCOPE_STATUS.ERROR]: 'Unable to load score',
});

function scopeStatusText(scope) {
  if (!scope || scope.status === HEALTH_SCOPE_STATUS.READY) return '';
  if (scope.status === HEALTH_SCOPE_STATUS.ERROR && scope.message) {
    return `${SCOPE_STATUS_TEXT[HEALTH_SCOPE_STATUS.ERROR]}: ${scope.message}`;
  }
  return SCOPE_STATUS_TEXT[scope.status] || SCOPE_STATUS_TEXT[HEALTH_SCOPE_STATUS.UNAVAILABLE];
}

const EndpointsGauge = ({
  isDarkMode = true,
  grade = null,
  scorePercent = 0,
  hasData = true,
  statusText = '',
}) => {
  /* Empty / loading: keep ticks neutral (no grade colour) and needle at rest. */
  const clampedScore = hasData
    ? Math.max(0, Math.min(100, Number(scorePercent) || 0))
    : 0;
  const [animatedScore, setAnimatedScore] = useState(0);

  useEffect(() => {
    if (!hasData) {
      setAnimatedScore(0);
      return undefined;
    }

    if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      setAnimatedScore(clampedScore);
      return undefined;
    }

    const durationMs = 1200;
    const start = performance.now();
    let rafId = 0;

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.max(0, Math.min(1, elapsed / durationMs));
      const eased = easeOutCubic(progress);
      setAnimatedScore(clampedScore * eased);
      if (progress < 1) rafId = window.requestAnimationFrame(tick);
    };

    setAnimatedScore(0);
    rafId = window.requestAnimationFrame(tick);
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [clampedScore, hasData]);

  const displayScore = Math.round(clampedScore);
  const displayGrade = hasData ? (String(grade || '').trim() || '—') : '—';
  /* Keep dial flush in the crop — positive offsets clipped the hub under overflow:hidden. */
  const dialOffsetY = 0;
  const activeStroke = hasData
    ? getGaugeActiveStroke(displayScore, grade)
    : (isDarkMode ? '#404040' : '#c4c4c4');
  const ticks = useMemo(
    () => buildGaugeTicks({
      count: ENDPOINTS_GAUGE_VIEW.tickCount,
      cx: ENDPOINTS_GAUGE_VIEW.cx,
      cy: ENDPOINTS_GAUGE_VIEW.cy,
      innerRadius: ENDPOINTS_GAUGE_VIEW.tickInnerRadius,
      outerRadius: ENDPOINTS_GAUGE_VIEW.tickOuterRadius,
      scorePercent: hasData ? animatedScore : 0,
    }),
    [animatedScore, hasData]
  );
  const inactiveTickStroke = isDarkMode ? '#404040' : '#c4c4c4';
  const ariaLabel = hasData
    ? `Security score ${displayScore} percent, rating ${displayGrade}`
    : `Security score: ${statusText || 'unavailable'}`;
  return (
    <div
      className={`health-score-radar-shell${hasData ? '' : ' health-score-radar-shell--empty'}`}
      id="health-gauge-overall"
      data-health-metric="overall"
      data-health-grade={displayGrade}
      data-health-score={hasData ? String(displayScore) : ''}
      data-health-empty={hasData ? 'false' : 'true'}
      data-gauge-theme={isDarkMode ? 'dark' : 'light'}
    >
      <div
        className="posture-dual__gauge-visual"
        style={{
          '--endpoints-label-anchor-y-pct': `${getEndpointsGaugeResolvedLabelAnchorYPct()}%`,
        }}
      >
        <div className="health-score-stage health-score-stage--endpoints">
          <div className="health-score-stage-bundle">
            <div
              className="endpoints-gauge-hover-value endpoints-gauge-hover-value--stacked"
              data-health-overall-readout="true"
              aria-hidden="true"
            >
              {hasData ? (
                <span className="endpoints-gauge-hover-value__readout-stack">
                  <span className="endpoints-gauge-hover-value__score-row">
                    <span className="endpoints-gauge-hover-value__score">{displayScore}</span>
                    <span className="endpoints-gauge-hover-value__unit">%</span>
                  </span>
                  <span className="endpoints-gauge-hover-value__score-label">Security Score</span>
                  <span className="endpoints-gauge-hover-value__rating-badge">
                    <span className="endpoints-gauge-hover-value__rating">Rating:</span>
                    <span className="endpoints-gauge-hover-value__grade" data-health-overall-grade={displayGrade}>
                      {displayGrade}
                    </span>
                  </span>
                </span>
              ) : (
                <span className="endpoints-gauge-hover-value__readout-stack">
                  <span className="endpoints-gauge-hover-value__score-row">
                    <span className="endpoints-gauge-hover-value__score">—</span>
                  </span>
                  <span className="endpoints-gauge-hover-value__score-label">Security Score</span>
                  <span className="endpoints-gauge-hover-value__rating-badge">
                    <span className="endpoints-gauge-hover-value__rating">Rating:</span>
                    <span className="endpoints-gauge-hover-value__grade" data-health-overall-grade="—">—</span>
                  </span>
                </span>
              )}
            </div>
            <svg
              id="endpoints-gauge-svg"
              viewBox={`${ENDPOINTS_GAUGE_VIEW.viewBoxX} ${ENDPOINTS_GAUGE_VIEW.viewBoxY} ${ENDPOINTS_GAUGE_VIEW.viewBoxWidth} ${ENDPOINTS_GAUGE_VIEW.viewBoxHeight}`}
              className="health-score-radar-svg endpoints-gauge-svg"
              data-gauge-root="endpoints"
              data-gauge-svg-section="root"
              preserveAspectRatio="xMidYMid meet"
              role="img"
              aria-label={ariaLabel}
            >
              <EndpointsGaugeDefs />
              <g id="endpoints-gauge-paint-stack" data-gauge-svg-section="paint-stack">
                <g id="endpoints-gauge-layer-dial" transform={`translate(0 ${dialOffsetY})`}>
                  <g
                    id="endpoints-gauge-layer-ticks"
                    className="endpoints-gauge-svg-layer"
                    data-gauge-svg-layer="ticks"
                    data-gauge-z-index="3"
                  >
                    <EndpointsGaugeTicks
                      ticks={ticks}
                      activeStroke={activeStroke}
                      inactiveStroke={inactiveTickStroke}
                    />
                  </g>
                </g>
              </g>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Security Health card. Displays backend health scores only
 * (see `selectHealthScopes`); never calculates a score or grade in the browser.
 */
const HealthScore = ({
  summary,
  isDarkMode = true,
  isLoading = false,
  loadError = null,
  isDateRangeScoped = false,
  healthV12Enabled,
}) => {
  const healthV12 = resolveHealthV12Mode({ enabled: healthV12Enabled, summary });
  const scopes = useMemo(
    () => selectHealthScopes(summary, { isLoading, error: loadError, healthV12 }),
    [summary, isLoading, loadError, healthV12]
  );
  const overall = scopes.overall;
  const ready = overall.status === HEALTH_SCOPE_STATUS.READY;
  const scorePercent = ready ? Math.round(Math.max(0, Math.min(100, overall.score))) : 0;
  const statusText = scopeStatusText(overall);
  const coverageSummary = useMemo(() => summarizeCoverageLayers(scopes), [scopes]);
  const coverageCopy = useMemo(() => coverageCalloutCopy(coverageSummary), [coverageSummary]);
  const showCoverageBanner = coverageSummary.activeCount < coverageSummary.totalCount;

  return (
    <div
      className="health-score-container health-score-container--reference"
      data-health-status={overall.status}
      data-health-formula-version={overall.formulaVersion || undefined}
    >
      <div className="posture-dual__column-body health-score-card-body">
        <h2 className="dashboard-posture-heading">
          Security Health
          <InfoTooltip label="About the Security Health Score" title="How your Security Health Score works">
            <p>
              Your score (0–100%) reflects the open vulnerabilities found in your most recent completed
              scans. Critical and High findings have the biggest impact. Medium and Low findings still
              count, but large numbers of them won&apos;t outweigh serious issues. A completed scan with
              no open findings scores 100%.
            </p>
            <p>
              Fixing Critical and High issues improves your score the most. The score only covers the
              services currently scanning your environment, so it&apos;s a guide to your measured risk,
              not a guarantee that you&apos;re secure.
            </p>
          </InfoTooltip>
        </h2>
        <EndpointsGauge
          isDarkMode={isDarkMode}
          grade={ready ? overall.grade : null}
          scorePercent={scorePercent}
          hasData={ready}
          statusText={statusText}
        />
        {showCoverageBanner ? (
          <aside className="health-score-info-banner" data-testid="health-coverage-banner">
            <span className="health-score-info-banner__icon" aria-hidden="true">
              <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6">
                <circle cx="8" cy="8" r="6.25" />
                <path d="M8 7.2V11M8 5.2h.01" strokeLinecap="round" />
              </svg>
            </span>
            <p className="health-score-info-banner__body">{coverageCopy.body}</p>
          </aside>
        ) : null}
      </div>
      {ready && isDateRangeScoped ? (
        <div className="health-score-footnote" data-health-part="footnote">
          <span
            className="health-score-footnote__current"
            title="Health score reflects your current security posture, not the selected date range."
          >
            Current score{overall.label ? ` · ${overall.label}` : ''}
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default HealthScore;

